import { AdvancedMarker, APIProvider, Map, useMap } from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useState, useRef, Component, ErrorInfo, ReactNode } from 'react'
import './map-animations.css'

interface ILocation {
  lat: number
  lng: number
}

export interface IRestaurantMapData {
  id: number
  name: string
  rating?: number
  location: ILocation
  awards?: string[]
}

interface MapComponentProps {
  restaurants: IRestaurantMapData[]
  selectedId: number | null
  hoveredId?: number | null
  initialCenter?: ILocation
  userLocation?: ILocation | null
  onBoundsChange: (bounds: google.maps.LatLngBounds | null, center?: google.maps.LatLng | null, map?: google.maps.Map) => void
  onMarkerClick: (id: number) => void
  panToLocation?: ILocation | null
  boundsToFit?: google.maps.LatLngBoundsLiteral | null
  onMapReady?: (map: google.maps.Map | null) => void
}

/** 店铺图钉 — 点击变色，hover 显示店名 */
function ShopPin({ id, name, isSelected, isHovered, awards, onHover, onLeave, onSelect, style }: {
  id: number
  name: string
  isSelected: boolean
  isHovered: boolean
  awards?: string[]
  onHover: () => void
  onLeave: () => void
  onSelect: () => void
  style?: React.CSSProperties
}) {
  const active = isSelected || isHovered

  const getAwardColor = () => {
    if (!awards || awards.length === 0) return ''
    const award = awards[0]
    if (!award || typeof award !== 'string') return ''
    const lower = award.toLowerCase()
    if (lower.includes('gold') || lower.includes('金')) return 'bg-[#D4AF37]'
    if (lower.includes('silver') || lower.includes('銀')) return 'bg-[#C0C0C0]'
    if (lower.includes('bronze') || lower.includes('銅')) return 'bg-[#CD7F32]'
    if (lower.includes('百名店') || lower.includes('100')) return 'bg-danger-600'
    return ''
  }

  const awardColor = getAwardColor()
  const defaultBg = active ? 'bg-primary' : (awardColor ? awardColor : 'bg-red-500')
  const defaultRing = awardColor ? awardColor.replace('bg-', 'bg-').replace('500', '500/40') : 'bg-red-500/40' // Fallback for ring


  return (
    <button
      type="button"
      aria-label={name}
      data-testid={`shop-pin-${id}`}
      className="relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-full shop-pin-enter focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      style={style}
      onClick={onSelect}
      onPointerEnter={onHover}
      onPointerLeave={onLeave}
    >
      {/* 浮窗标签 — hover/selected 时展示店名 */}
      {active && (
        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap z-10">
          <div className={`
            px-2.5 py-1 rounded-lg text-xs font-semibold shadow-lg
            ${active
              ? 'bg-primary text-white'
              : 'bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100'
            }
          `}>
            <span className="line-clamp-1 max-w-[160px] block">{name}</span>
          </div>
          <div className={`w-2 h-2 rotate-45 mx-auto -mt-1 ${
            active ? 'bg-primary' : 'bg-white dark:bg-stone-800'
          }`} />
        </div>
      )}

      {/* 图钉圆点 — pointer-events 仅在此圆点上生效 */}
      <div
        className={`
          pointer-events-none w-5 h-5 rounded-full border-[2.5px] border-white shadow-md
          transition-all duration-300 ease-out active:scale-95
          ${active 
            ? `scale-[1.5] ring-2 ring-white shadow-xl ${awardColor || 'bg-primary'}` 
            : `scale-100 ${defaultBg}`
          }
        `}
      />
      {/* Pulsing ring on hover or selected */}
      {(isHovered || isSelected) && (
        <div 
          className={`absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full animate-ping opacity-40 pointer-events-none ${isSelected ? (awardColor || 'bg-primary') : defaultBg}`}
          style={{ animationDuration: '2s' }} 
        />
      )}
    </button>
  )
}

/** 用户位置蓝点标记 — 类似 Google Maps 样式 */
function UserLocationPin() {
  return (
    <div className="relative flex items-center justify-center">
      {/* 外圈呼吸脉冲 */}
      <div className="absolute w-10 h-10 rounded-full bg-blue-500/15 animate-ping" style={{ animationDuration: '2s' }} />
      {/* 蓝色光圈 */}
      <div className="absolute w-7 h-7 rounded-full bg-blue-500/20" />
      {/* 核心蓝点 */}
      <div className="w-4 h-4 rounded-full bg-blue-600 border-[2.5px] border-white shadow-lg relative z-10" />
    </div>
  )
}

function MapInner(props: MapComponentProps) {
  const { restaurants, selectedId, hoveredId: externalHoveredId, initialCenter, userLocation, onBoundsChange, onMarkerClick, boundsToFit, onMapReady } = props
  const map = useMap()
  const [internalHoveredId, setInternalHoveredId] = useState<number | null>(null)
  const pendingBoundsMap = useRef<google.maps.Map | null>(null)
  const boundsFrame = useRef<number | null>(null)
  
  const hoveredId = externalHoveredId !== undefined && externalHoveredId !== null ? externalHoveredId : internalHoveredId

  useEffect(() => {
    onMapReady?.(map || null)
    return () => onMapReady?.(null)
  }, [map, onMapReady])

  // Removed automatic map.panTo on selectedId change to prevent unexpected map shifting 
  // when users click markers or when the sidebar scrolls to active items.

  useEffect(() => {
    if (map && props.panToLocation) {
      map.panTo(props.panToLocation)
      map.setZoom(15)
    }
  }, [map, props.panToLocation])

  useEffect(() => {
    if (map && boundsToFit) {
      map.fitBounds(boundsToFit, 60) // 60px padding
    }
  }, [map, boundsToFit])

  const handleHover = useCallback((id: number) => setInternalHoveredId(id), [])
  const handleLeave = useCallback(() => setInternalHoveredId(null), [])
  const handleBoundsChanged = useCallback((event: { map: google.maps.Map }) => {
    pendingBoundsMap.current = event.map
    if (boundsFrame.current !== null) return

    boundsFrame.current = window.requestAnimationFrame(() => {
      boundsFrame.current = null
      const latestMap = pendingBoundsMap.current
      if (!latestMap) return

      onBoundsChange(
        latestMap.getBounds() || null,
        latestMap.getCenter() || null,
        latestMap,
      )
    })
  }, [onBoundsChange])

  useEffect(() => {
    return () => {
      if (boundsFrame.current !== null) {
        window.cancelAnimationFrame(boundsFrame.current)
      }
    }
  }, [])

  return (
    <Map
      defaultZoom={13}
      defaultCenter={initialCenter || { lat: 35.6895, lng: 139.6917 }}
      gestureHandling="greedy"
      disableDefaultUI
      mapId="DEMO_MAP_ID"
      onBoundsChanged={handleBoundsChanged}
    >
      {/* 用户位置标记 */}
      {map && userLocation && (
        <AdvancedMarker position={userLocation} zIndex={200}>
          <UserLocationPin />
        </AdvancedMarker>
      )}

      {/* 店铺标记 */}
      {map && restaurants.map((restaurant, index) => {
        const isSelected = String(selectedId) === String(restaurant.id)
        const isHovered = String(hoveredId) === String(restaurant.id)
        return (
          <AdvancedMarker
            key={restaurant.id}
            position={restaurant.location}
            zIndex={isSelected ? 100 : isHovered ? 50 : 1}
            clickable
            onClick={() => onMarkerClick(Number(restaurant.id))}
          >
            <ShopPin
              id={restaurant.id}
              name={restaurant.name}
              isSelected={isSelected}
              isHovered={isHovered}
              awards={restaurant.awards}
              onHover={() => handleHover(restaurant.id)}
              onLeave={handleLeave}
              onSelect={() => onMarkerClick(Number(restaurant.id))}
              style={{ animationDelay: `${Math.min(index * 30, 500)}ms` }}
            />
          </AdvancedMarker>
        )
      })}
    </Map>
  )
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class MapErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null
  }

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Google Maps Error caught by Boundary:', error, errorInfo)
  }

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="w-full h-full flex flex-col items-center justify-center bg-stone-200 dark:bg-stone-800 text-stone-600 dark:text-stone-400 p-4 text-center">
            <p className="text-sm font-medium mb-2">Map view is temporarily unavailable</p>
            <p className="text-xs opacity-70 max-w-[280px] break-words">
              {this.state.error?.message || 'Initialization failed'}
            </p>
          </div>
        )
      )
    }

    return this.props.children
  }
}

export function MapComponent(props: MapComponentProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  const [authFailed, setAuthFailed] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        return window.sessionStorage?.getItem('tb2_gm_auth_failed') === 'true'
      } catch (e) {}
    }
    return false
  })

  useEffect(() => {
    // Intercept Google Maps Auth Failures
    const prevAuthFailure = (window as any).gm_authFailure
    ;(window as any).gm_authFailure = () => {
      setAuthFailed(true)
      try {
        window.sessionStorage?.setItem('tb2_gm_auth_failed', 'true')
      } catch (e) {}
      if (prevAuthFailure) prevAuthFailure()
    }

    return () => {
      ;(window as any).gm_authFailure = prevAuthFailure
    }
  }, [])

  if (!apiKey) {
    return <div className="w-full h-full flex items-center justify-center bg-stone-200">Missing VITE_GOOGLE_MAPS_API_KEY</div>
  }

  if (authFailed) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400 mb-3 animate-bounce">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <p className="text-sm font-semibold mb-1 text-stone-800 dark:text-stone-200">Google Maps Authentication Failed</p>
        <p className="text-xs opacity-75 max-w-[280px] leading-relaxed">
          The API key has expired (ExpiredKeyMapError) or is invalid. Please check and configure a valid <strong>VITE_GOOGLE_MAPS_API_KEY</strong> in <code>apps/web/.env</code>.
        </p>
      </div>
    )
  }

  return (
    <MapErrorBoundary>
      <APIProvider apiKey={apiKey}>
        <MapInner {...props} />
      </APIProvider>
    </MapErrorBoundary>
  )
}
